/**
 * Comentários do cliente — abaixo da timeline no modal de lead
 */

import { useState, type KeyboardEvent } from 'react';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { message } from 'antd';
import { formatDate } from '@/lib/utils';
import {
  formatAutorNome,
  type ClienteComentario,
} from '../services/clienteComentarios.service';
import { useClienteComentarios } from '../hooks/useClienteComentarios';

interface ClienteComentariosSectionProps {
  clienteId?: string;
  cardId?: string;
  enabled?: boolean;
}

function ComentarioItem({ item }: { item: ClienteComentario }) {
  return (
    <div className="px-3 py-2.5 border-b border-[#eceef2] dark:border-zinc-800 last:border-b-0">
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-[12px] font-medium text-gray-800 dark:text-gray-200">
          {formatAutorNome(item.autor)}
        </span>
        <span className="text-[10px] text-[#8f9aab] shrink-0">
          {formatDate(item.created_at, 'dd/MM/yyyy HH:mm')}
        </span>
      </div>
      <p className="text-[13px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
        {item.conteudo}
      </p>
    </div>
  );
}

export function ClienteComentariosSection({
  clienteId,
  cardId,
  enabled = true,
}: ClienteComentariosSectionProps) {
  const [texto, setTexto] = useState('');
  const { comentarios, loading, saving, addComentario } = useClienteComentarios(
    clienteId,
    enabled
  );

  const handleSubmit = async () => {
    const conteudo = texto.trim();
    if (!conteudo) return;

    try {
      await addComentario({ conteudo, cardId });
      setTexto('');
      message.success('Comentário registrado');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao salvar comentário');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  if (!clienteId) {
    return (
      <div className="border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 flex-shrink-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Vincule um cliente para registrar comentários
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 flex flex-col min-h-0 max-h-[240px]">
      <div className="px-4 py-2 border-b border-[#eceef2] dark:border-zinc-800 flex items-center gap-2 flex-shrink-0">
        <MessageSquare className="w-3.5 h-3.5 text-[#8f9aab]" />
        <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          Comentários
        </h4>
        {comentarios.length > 0 && (
          <span className="text-[10px] text-[#8f9aab]">({comentarios.length})</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 bg-[#fafbfc] dark:bg-zinc-950/50">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-[#8f9aab]" />
          </div>
        ) : comentarios.length === 0 ? (
          <p className="text-[12px] text-[#8f9aab] text-center py-5 px-4">
            Nenhum comentário ainda. Registre observações sobre este cliente.
          </p>
        ) : (
          <div>
            {comentarios.map((item) => (
              <ComentarioItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-[#eceef2] dark:border-zinc-800 flex-shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escreva um comentário sobre o cliente..."
            rows={2}
            className="flex-1 resize-none rounded-lg border border-[#dce3ef] dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-[13px] text-gray-800 dark:text-gray-200 placeholder:text-[#8f9aab] focus:outline-none focus:ring-2 focus:ring-[#FF3737]/30 focus:border-[#FF3737]"
          />
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || !texto.trim()}
            className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-[#FF3737] hover:bg-[#D92B2B] disabled:opacity-50 text-white text-[12px] font-medium transition-colors"
            title="Enviar (Ctrl+Enter)"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Enviar
          </button>
        </div>
        <p className="text-[10px] text-[#8f9aab] mt-1.5">Ctrl+Enter para enviar</p>
      </div>
    </div>
  );
}

export default ClienteComentariosSection;
